<script lang="ts">
// this must be exported here for the keep alive on reroute
export default {
  name: "Region",
};
</script>

<script setup lang="ts">
import RegionTable from "@/components/models/region/RegionTable.vue";
import { useSnackStore } from "@/stores/snack";
import { useQuery } from "@tanstack/vue-query";
import { definePage } from "unplugin-vue-router/runtime";
import { onMounted, provide, ref } from "vue";

import RegionService, { type TRegion } from "@/api/region";
import { ActionsEnum, SubjectsEnum, type AppAbility } from "@/services/ability";
import { generateFormOptions, type PaginatedType, type VFormOptions } from "@/utils/DjangoTypes";
import { useAbility } from "@casl/vue";
import { DefaultServerTableParams, type ServerTableParamsType } from "@/utils/TableUtils";

definePage({
  meta: {
    title: "Region",
    checkPrivileges: () => {
      const { can } = useAbility<AppAbility>();
      return can(ActionsEnum.USE, SubjectsEnum.ROLE);
    },
  },
});

const snackStore = useSnackStore();
const fetchEnabled = ref(false);

const options = ref<VFormOptions>({});

const queriesTableParams: Record<string, ServerTableParamsType> = {
  regions: DefaultServerTableParams,
};
provide("queriesTableParams", queriesTableParams);

const {
  data: regions,
  isPending: isPending,
  isFetching: isFetching,
} = useQuery({
  queryKey: ["regions"],
  queryFn: async (): Promise<PaginatedType<TRegion[]> | undefined> => {
    try {
      const [result, restOptions] = await Promise.all([
        RegionService.region.getAll(queriesTableParams.regions),
        RegionService.region.getOptions(),
      ]);

      options.value = generateFormOptions(restOptions.data.actions.POST);

      return result.data;
    } catch (error) {
      snackStore.showError(error);
    }
  },
  enabled: () => fetchEnabled.value,
});

onMounted(async () => {
  fetchEnabled.value = true;
});
</script>

<template>
  <v-container fluid>
    <div class="tw:!mb-10">
      <region-table
        :query-key="'regions'"
        :data="regions"
        :loading="isPending || isFetching"
        :options="options"
      />
    </div>
  </v-container>
</template>

<style scoped></style>
